import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import zod from 'zod';

import { t } from '../../core/logical/type-presets.js';
import { propertyType } from '../../core/property/property-config.js';
import { dayOfLocalDate, localDateOfDay } from './calendar-day.js';
export const datePropertyType = propertyType('date');

export type DatePropertyData = {
  includeTime?: boolean;
  timezoneMode?: 'workspace' | 'user' | 'explicit';
};

const DATE_ONLY_FORMAT = 'yyyy-MM-dd';
const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';

export const datePropertyModelConfig = datePropertyType.modelConfig({
  name: 'Date',
  propertyData: {
    schema: zod.object({
      includeTime: zod.boolean().optional(),
      timezoneMode: zod.enum(['workspace', 'user', 'explicit']).optional(),
    }),
    default: (): DatePropertyData => ({}),
  },
  jsonValue: {
    schema: zod.number().nullable(),
    isEmpty: () => false,
    type: ({ data }) =>
      t.date.instance(data.includeTime ? undefined : { day: true }),
  },
  rawValue: {
    schema: zod.number().nullable(),
    default: () => null,
    toString: ({ value, data }) =>
      value == null
        ? ''
        : data.includeTime
          ? format(value, DATE_TIME_FORMAT)
          : format(localDateOfDay(value), DATE_ONLY_FORMAT),
    fromString: ({ value, data }) => {
      const trimmed = value.trim();
      const date = parse(
        trimmed,
        trimmed.includes(':') ? DATE_TIME_FORMAT : DATE_ONLY_FORMAT,
        new Date()
      );
      if (!isValid(date)) {
        return { value: null };
      }
      return { value: data.includeTime ? +date : dayOfLocalDate(date) };
    },
    toJson: ({ value }) => value,
    fromJson: ({ value }) => value,
  },
});
